import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  GraduationCap,
  Clock,
  Globe,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  { icon: GraduationCap, label: "Mentorship", desc: "Hands-on training from licensed leaders." },
  { icon: Clock, label: "Flexible Schedule", desc: "Work part-time or full-time on your terms." },
  { icon: Globe, label: "Remote Opportunities", desc: "Build your business from anywhere." },
  { icon: TrendingUp, label: "High Commissions", desc: "Uncapped income with bonus tiers." },
];

export default function RecruitmentSection() {
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    phone: "",
    current_occupation: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads/recruitment`, form);
      toast.success(res.data?.message || "Application received.");
      setForm({ first_name: "", email: "", phone: "", current_occupation: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="careers"
      data-testid="recruitment-section"
      className="relative py-24 md:py-40 overflow-hidden"
    >
      {/* Backdrop image */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <img
          src={SITE.images.architecture1}
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        <div className="absolute inset-0 ambient-radial opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl reveal">
          <div className="text-[11px] uppercase tracking-[0.28em] text-rolex-light mb-6">
            Careers
          </div>
          <h2 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
            Build an income stream in the{" "}
            <span className="italic text-rolex-light">insurance industry.</span>
          </h2>
          <p className="mt-8 text-lg text-gray-400 max-w-xl leading-relaxed font-light">
            No experience required. Training provided. Flexible schedule.
            Unlimited income potential.
          </p>
        </div>

        <div className="mt-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-5">
              {features.map((f, i) => (
                <div
                  key={f.label}
                  data-testid={`recruitment-feature-${i}`}
                  className="reveal rounded-3xl glass p-7 hover:-translate-y-1 hover:border-rolex/40 transition-all duration-500"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-rolex/10 border border-rolex/30 mb-5">
                    <f.icon className="h-5 w-5 text-rolex-light" />
                  </div>
                  <div className="font-serif text-2xl text-white mb-1.5">
                    {f.label}
                  </div>
                  <div className="text-sm text-gray-400 leading-relaxed font-light">
                    {f.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="recruitment-form"
            className="reveal lg:col-span-5 relative rounded-3xl glass p-8 md:p-10"
            style={{ transitionDelay: "120ms" }}
          >
            <div
              aria-hidden
              className="absolute -inset-px rounded-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(120% 80% at 50% 0%, rgba(0,98,65,0.18), rgba(0,0,0,0) 60%)",
              }}
            />
            <div className="relative space-y-5">
              <div className="font-serif text-2xl text-white">Apply Now</div>
              <p className="text-sm text-gray-500 font-light -mt-2">
                Tell us about yourself. We'll reach out with next steps.
              </p>

              <Field
                label="First Name"
                testid="recruitment-input-first-name"
                value={form.first_name}
                onChange={update("first_name")}
                placeholder="Alex"
              />
              <Field
                label="Email"
                type="email"
                testid="recruitment-input-email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@email.com"
              />
              <Field
                label="Phone Number"
                type="tel"
                testid="recruitment-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+1 (555) 000-0000"
              />
              <Field
                label="Current Occupation"
                testid="recruitment-input-occupation"
                value={form.current_occupation}
                onChange={update("current_occupation")}
                placeholder="e.g. Sales / Teacher / Nurse"
              />

              <button
                type="submit"
                disabled={submitting}
                data-testid="recruitment-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#006241] hover:bg-[#0a8a5e] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full text-[15px] font-medium transition-all duration-300 shadow-[0_0_40px_rgba(0,98,65,0.35)] hover:shadow-[0_0_60px_rgba(0,98,65,0.55)]"
              >
                {submitting ? "Submitting..." : "Apply Now"}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.28em] text-gray-500 mb-2.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.03] border border-white/10 focus:border-rolex focus:bg-white/[0.05] focus:outline-none rounded-2xl px-5 py-3.5 text-white placeholder:text-gray-600 transition-all duration-300"
      />
    </div>
  );
}
